import React, { createContext, useContext, useState, useCallback } from 'react';
import { handleDeleteBatch, handleRestoreBatch, handleDownloadOriginal, handleDownloadLedgerHistory, handleDownloadReport } from '../../Helper';
import UserProfile from '../auth/UserProfile';
import { LINKS_PER_PAGE, Role_Admin } from '../../constants';
import classNames from 'classnames';
const UploadHistoryContext = createContext();

export const UploadHistoryProvider = ({ children, manufacturerId: mid,classes ,refetch,onDone }) => {
    const [dialogContent, setDialogContent] = useState({
        title: '',
        body: '',
        actionType: 0,
        actionButtonTitle: 'Confirm',
        uploadId: 0,
        message: null
    });
    const [deleteReason, setDeleteReason] = useState('');
    const [customDeleteReason, setCustomDeleteReason] = useState('');
    const [dialogProgress, setDialogProgress] = useState(false);
    const [isDownloadOriginalInProgress, setDownloadOriginalInProgress] = useState(false);
    const [isDownloadHistoricInProgress, setDownloadHistoricInProgress] = useState(false);
    const [isDownloadLedgerInProgress, setDownloadLedgerInProgress] = useState(false);
    const [hasError, setError] = useState(false);
    const [activeUploadId, setActiveUploadId] = useState(null);
    const [dialogState, setDialogState] = useState(false);
    const [currentFilename, setCurrentFilename] = useState('');
   
    const isAdmin = (Role_Admin === UserProfile.getRole());

    

    const getManufacturerId = useCallback(() => (
        Role_Admin === UserProfile.getRole() ? mid : UserProfile.getManufacturerId()
    ), [mid]);

    const downloadOriginal = useCallback((batchId, fileName,manufacturerId, uploadId) => {
        setDownloadOriginalInProgress(true);
        setActiveUploadId(uploadId);
        const extension = "." + fileName.split('.').pop();
        const contentType = extension.toLowerCase() === ".csv" ? "text/csv" : "application/excel";
        handleDownloadOriginal(batchId, manufacturerId, fileName, extension, contentType)
            .then(() => resetDownloadState(false))
            .catch(() => resetDownloadState(false));
    }, [getManufacturerId]);

    const downloadLedgerReport = useCallback((batchId, fileName, manufacturerId, uploadId) => {
        setDownloadLedgerInProgress(true);
        setActiveUploadId(uploadId);
        const extension = "." + fileName.split('.').pop();
        const contentType = extension.toLowerCase() === ".csv" ? "text/csv" : "application/excel";
        handleDownloadLedgerHistory(batchId, manufacturerId, fileName, extension, contentType)
            .then(() => resetDownloadState(false,true))
            .catch(() => resetDownloadState(false,true));
    }, [getManufacturerId]);

    const downloadHistoric = useCallback((batchId, fileName, manufacturerId, uploadId) => {
        setDownloadHistoricInProgress(true);
        setActiveUploadId(uploadId);
        const extension = "." + fileName.split('.').pop();
        const contentType = extension.toLowerCase() === ".csv" ? "text/csv" : "application/excel";
        handleDownloadReport(batchId, manufacturerId, fileName, extension, contentType)
            .then(() => resetDownloadState(true))
            .catch(() => resetDownloadState(true));
    }, [getManufacturerId]);

    const resetDownloadState = useCallback((isHistoric, isLedger) => {
        if (isLedger) {
            setDownloadLedgerInProgress(false);
        }else if (isHistoric) {
            setDownloadHistoricInProgress(false);
        } else {
            setDownloadOriginalInProgress(false);
        }
        setActiveUploadId(null);
    }, []);

    const handleDialogClose = useCallback(() => {
        setDialogState(false);
        setDialogProgress(false);
        setDialogContent({ ...dialogContent, message: null });
        setError(false);
        setDeleteReason('');
        setCustomDeleteReason('');
        setCurrentFilename('');
    }, [dialogContent]);

    const handleDelete = useCallback((uploadId, filename) => {
        setError(true);
        setCurrentFilename(filename);
        setDialogContent({
            title: 'Delete existing data',
            body: deleteAlertContent(filename),
            actionType: 1,
            uploadId: uploadId,
            actionButtonTitle: 'Delete',
        });
        setDialogState(true);
    }, []);

    const handleRestore = useCallback((uploadId, filename) => {
        setDialogContent({
            title: 'Restore deleted data',
            body: restoreAlertContent(filename),
            actionType: 2,
            uploadId: uploadId,
            actionButtonTitle: 'Restore',
        });
        setDialogState(true);
    }, []);
    
    const deleteAlertContent = useCallback((filename) => (
        <div className={classes.dialogContentContainer}>
            <span className={classes.subheaderLarge}>
                Are you sure you want to delete the uploaded product serials data?
            </span>
            <br /> This action cannot be undone.
            <br /> Please review the following details before proceeding:
            <div className={classes.dialogFileInfo}>
                <p>File to be deleted: <span className={classes.dialogFileName}>{filename}</span></p>
                <p>Shared with CER Serial Ledger: <span className={classes.dialogFileName}>Yes</span></p>
            </div>

            <div style={{ marginTop: '16px' }}>
                <label><strong>Reason for deletion: <span style={{ color: 'red' }}>*</span></strong></label>
                <select
                    className={classes.selectInput}
                    value={deleteReason}
                    onChange={(e) => { 
                        setDeleteReason(e.target.value); 
                        if (e.target.value && e.target.value !== "") {
                            setError(false);
                        }
                    }}
                    required
                >
                    <option value="">-- Select a reason --</option>
                    <option value="Ineligible product – Not for Australian market">Ineligible product – Not for Australian market</option>
                    <option value="Product recalled – Defective panel">Product recalled – Defective panel</option>
                    <option value="Product recalled – Damaged panel">Product recalled – Damaged panel</option>
                    <option value="Data error – Incorrect supplier">Data error – Incorrect supplier</option>
                    <option value="Data error – Incorrect model">Data error – Incorrect model</option>
                    <option value="Data error – Invalid serial number">Data error – Invalid serial number</option>
                    <option value="Other">Other</option>
                </select>
            </div>

            {deleteReason === 'Other' && (
                <div style={{ marginTop: '12px' }}>
                    <label><strong>Please specify the reason:</strong></label>
                    <textarea
                        className={classes.textAreaInput}
                        value={customDeleteReason}
                        onChange={(e) => {
                            setCustomDeleteReason(e.target.value);
                            if (e.target.value.trim()) {
                                setError(false);
                            }
                        }}
                        rows={3}
                        placeholder="Enter your reason here..."
                        required
                    />
                </div>
            )}

            <p style={{ marginTop: '16px' }}>
                Please ensure that you have carefully reviewed the uploaded data and confirmed that it is indeed the correct data to delete.
            </p>
        </div>
    ), [deleteReason, customDeleteReason, classes]);

    const restoreAlertContent = useCallback((filename) => (
        <div className={classes.dialogContentContainer}>
            <span className={classes.subheaderLarge}>
                Are you sure you want to restore the deleted product serials data?
            </span>
            <br /> This action cannot be undone.
            <br /> Please review the following details before proceeding: <br />
            <div className={classes.dialogFileInfo}>
                <p>File to be restored: <span className={classes.dialogFileName}>{filename}</span> </p>
            </div>
            <br />Please ensure that you have carefully reviewed the <strong>deleted data</strong> and confirmed that it is indeed the correct data to <strong>restore</strong>.
        </div>
    ), [classes]);
    const handleDialogAction = useCallback(() => {
        
        if (!dialogContent.uploadId) return;
        const actionHandler = dialogContent.actionType === 1 ? handleDeleteBatch : handleRestoreBatch;

        if (dialogContent.actionType === 1) {
            if (!deleteReason || deleteReason==="") {
                setError(true);
                setDialogContent({ ...dialogContent, message: 'Please select a reason for deletion.' });
                return;
            }

            if (deleteReason === 'Other' && !customDeleteReason.trim()) {
                setError(true);
                setDialogContent({ ...dialogContent, message: 'Please specify your custom reason.' });
                return;
            }
            
            // Clear any previous error messages if validation passes
            setError(false);
            setDialogContent({ ...dialogContent, message: null });
        }
        const reasonToSend = deleteReason === 'Other' ? customDeleteReason : deleteReason;
        setDialogProgress(true);
        actionHandler(dialogContent.uploadId, reasonToSend)
            .then(() => {
                refetch();
                handleDialogClose();
                onDone && onDone();
            })
            .catch((error) => {
                setDialogProgress(false);
                if (error?.status === false) {
                    setError(true);
                    setDialogContent({ ...dialogContent, message: error.message });
                    setTimeout(() => {}, 4000);
                }
            });
    }, [dialogContent, handleDialogClose, deleteReason, customDeleteReason]);

    // Add a useEffect to update dialogContent when deleteReason or customDeleteReason changes
    React.useEffect(() => {
        if (dialogContent.actionType === 1 && currentFilename) {
            setDialogContent({
                ...dialogContent,
                body: deleteAlertContent(currentFilename)
            });
        }
    }, [deleteReason, customDeleteReason, currentFilename]);

    return (
        <UploadHistoryContext.Provider
            value={{
               
               
                downloadOriginal,
                downloadHistoric,
                downloadLedgerReport,
                handleDelete,
                handleRestore,
                handleDialogAction,
                handleDialogClose,
                dialogContent,
                dialogProgress,
                isDownloadOriginalInProgress,
                isDownloadHistoricInProgress,
                isDownloadLedgerInProgress,
                activeUploadId,
                isAdmin,
                dialogState,
                hasError,
                restoreAlertContent,
                deleteAlertContent,
                deleteReason
            }}
        >
            {children}
        </UploadHistoryContext.Provider>
    );
};

export const useUploadHistory = () => useContext(UploadHistoryContext);
